<template>
    <div class="boundary-wizard">
        <!-- 조직 및 경계 관리 -->
        <div class="boundary-panel org-panel">
            <div class="panel-content">
                <div class="organization-section">
                    <!-- 통합 트리 테이블 -->
                    <boundary-tree-table
                        :loading="loading"
                        :loading-details="loadingOrgId != null"
                        :organizations="organizations"
                        :organizations-list="organizationsList"
                        @select-org="handleSelectOrg"
                        @add-organization="openOrgManagementModal"
                        @edit-organization="openOrgEditModal"
                        @edit-org-boundary="openOrgBoundaryEditModal"
                        @edit-oper-boundary="openOperBoundaryEditModal"
                        @add-org-boundary="openOrgBoundaryAddModal"
                        @add-oper-boundary="openOperBoundaryAddModal"
                        @organization-deleted="handleOrganizationDeleted"
                        @org-boundary-deleted="handleOrgBoundaryDeleted"
                        @oper-boundary-deleted="handleOperBoundaryDeleted"
                        @delete-error="handleDeleteError"
                        @update-org-status-local="updateOrgStatusLocal"
                        @update-boundary-status-local="updateBoundaryStatusLocal"
                        @org-updated="handleOrgUpdated"
                        @boundary-status-changed="handleBoundaryStatusChanged"
                        @status-toggle-error="handleStatusToggleError"
                        @evidence-organization="openOrgEvidenceModal"
                    />
                </div>
            </div>
        </div>

        <!-- 저장 버튼 -->
        <!-- <div class="action-buttons">
            <button class="action-btn btn-primary" @click="save" :disabled="saving">
                <i class="fas fa-save" v-if="!saving"></i>
                <i class="fas fa-spinner fa-spin" v-if="saving"></i>
                {{ saving ? "저장 중..." : "저장" }}
            </button>
        </div> -->

        <!-- 조직 추가 모달 (기존 통합 모달) -->
        <boundary-org-modal
            :show.sync="showOrgManagementModal"
            :mode="orgModalMode"
            :org-data="orgModalData"
            @update-org-field="updateOrgModalField"
            @saved="reloadOrganizations"
            @cancel="cancelOrgManagement"
        />

        <!-- 조직 편집 모달 -->
        <boundary-org-modal
            :show.sync="showOrgEditModal"
            mode="edit"
            :org-data="editingOrgData"
            @saved="handleOrgEditSaved"
        />

        <!-- 조직경계 편집 모달 -->
        <boundary-org-boundary-edit-modal
            :show.sync="showOrgBoundaryEditModal"
            :boundary-data="editingBoundaryData"
            :org-code="editingBoundaryOrgCode"
            @saved="handleOrgBoundaryEditSaved"
        />

        <!-- 운영경계 편집 모달 -->
        <boundary-oper-boundary-edit-modal
            :show.sync="showOperBoundaryEditModal"
            :boundary-data="editingOperBoundaryData"
            :org-code="editingOperBoundaryOrgCode"
            :master-categories="masterCategories"
            @saved="handleOperBoundaryEditSaved"
        />

        <!-- 조직 증빙 모달 -->
        <EvidenceModal
            :visible="showOrgEvidenceModal"
            evidence-category="ORG"
            :entity-idx="orgEvidenceTarget?.orgIdx ?? null"
            :modal-title-override="orgEvidenceTarget ? `증빙자료 - ${orgEvidenceTarget.orgName || orgEvidenceTarget.name || '조직'}` : ''"
            @close="showOrgEvidenceModal = false"
            @updated="handleOrgEvidenceUpdated"
        />
    </div>
</template>

<script>
import carbonApi from "@/submodules/carbon-plugin/api";
import { unwrapSuccess, extractErrorMessage } from "../../../utils/api.js";
import BoundaryOrgModal from "../modals/BoundaryOrgModal.vue";
import BoundaryTreeTable from "../views/BoundaryTreeTable.vue";
import BoundaryOrgBoundaryEditModal from "../modals/BoundaryOrgBoundaryEditModal.vue";
import BoundaryOperBoundaryEditModal from "../modals/BoundaryOperBoundaryEditModal.vue";
import EvidenceModal from "../../activity/modals/EvidenceModal.vue";
import CarbonCodes from "../../../codes";
import { getScope1Categories, getScope2Categories, getScope3Categories } from "../../../constants/codes.js";

export default {
    name: "BoundaryMain",
    components: {
        BoundaryOrgModal,
        BoundaryTreeTable,
        BoundaryOrgBoundaryEditModal,
        BoundaryOperBoundaryEditModal,
        EvidenceModal,
    },
    props: {
        loginUser: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            nextOrgId: 1,
            showOrgManagementModal: false,
            orgModalMode: "create",
            orgModalEditingId: null,

            // 조직 모달 데이터
            orgModalData: {
                name: "",
                orgCode: "",
                businessNumber: "",
                industry: "",
                address: "",
                remarks: "",
                contactPerson: "",
                contactEmail: "",
                contactPhone: "",
                fiscalYearEnd: "1231",
                accountingMethod: "FINANCIAL_CONTROL",
            },

            // 편집 모달 상태
            showOrgEditModal: false,
            editingOrgData: {},
            editingOrgId: null,

            showOrgBoundaryEditModal: false,
            editingBoundaryData: {},
            editingBoundaryOrgId: null,

            showOperBoundaryEditModal: false,
            editingOperBoundaryData: {},
            editingOperBoundaryOrgId: null,

            // 메인 데이터 구조
            organizations: {},

            loading: false, // 조직 목록 로딩
            loadingOrgId: null, // 선택된 조직의 경계 로딩 중인 orgId

            saving: false,

            // 마스터 카테고리 목록
            masterCategories: {
                scope1: [],
                scope2: [],
                scope3: [],
            },

            // 조직 증빙 모달
            showOrgEvidenceModal: false,
            orgEvidenceTarget: null,
        };
    },
    computed: {
        // 공통 코드
        boundarySetMethod() {
            return CarbonCodes.accountingMethod.getList();
        },

        // 조직 목록
        organizationsList() {
            if (!this.organizations) return [];

            return Object.entries(this.organizations).map(([id, data]) => ({
                id,
                name: data?.orgName || data?.name || "",
                orgIdx: data?.orgIdx || null,
                orgBoundariesCount: data?.orgBoundaries?.length || 0,
                operBoundariesCount: data?.operBoundaries?.length || 0,
            }));
        },

        // 조직이 있는지 여부
        hasOrganizations() {
            return this.organizationsList && this.organizationsList.length > 0;
        },

        // 편집 중인 조직의 orgCode
        editingBoundaryOrgCode() {
            if (!this.editingBoundaryOrgId) return null;
            const org = this.organizations[this.editingBoundaryOrgId];
            return org?.orgCode || null;
        },

        editingOperBoundaryOrgCode() {
            if (!this.editingOperBoundaryOrgId) return null;
            const org = this.organizations[this.editingOperBoundaryOrgId];
            return org?.orgCode || null;
        },
    },
    mounted() {
        this.loadOrganizations();
    },

    methods: {
        async reloadOrganizations() {
            try {
                this.organizations = {};
                await this.loadOrganizations();
            } catch (e) {
                console.error("[BoundaryMain] 조직 재조회 실패:", e);
            }
        },
        async reloadOrgBoundaries(orgIdx) {
            try {
                if (!orgIdx) return;
                const orgId = `org-${orgIdx}`;
                if (!this.organizations[orgId]) return;

                await this.loadOrgBoundaries(orgIdx, orgId);
            } catch (e) {
                console.error("[BoundaryMain] 조직경계 재조회 실패:", e);
            }
        },
        async reloadOperBoundaries(orgIdx) {
            try {
                if (!orgIdx) return;
                const orgId = `org-${orgIdx}`;
                if (!this.organizations[orgId]) return;

                await this.loadOperBoundaries(orgIdx, orgId);
            } catch (e) {
                console.error("[BoundaryMain] 운영경계 재조회 실패:", e);
            }
        },
        // ==================== 안전한 접근 헬퍼 ====================
        getOrganization(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return {};
            return this.organizations[orgId];
        },

        getOrgBoundaries(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return [];
            return this.organizations[orgId].orgBoundaries || [];
        },

        getOperBoundaries(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return [];
            return this.organizations[orgId].operBoundaries || [];
        },

        getOrgBoundariesCount(orgId) {
            return this.getOrgBoundaries(orgId).length;
        },

        getOperBoundariesCount(orgId) {
            return this.getOperBoundaries(orgId).length;
        },

        getTotalFacilitiesCount(orgId) {
            const boundaries = this.getOrgBoundaries(orgId);
            return boundaries.reduce((total, boundary) => {
                return total + (boundary?.facilities?.length || 0);
            }, 0);
        },

        // ==================== API 관련 ====================
        async loadOrganizations() {
            this.loading = true;
            try {
                const apiData = unwrapSuccess(await carbonApi.organization.getOrgs(), "조직 목록 조회에 실패했습니다.");

                if (apiData && apiData.organizations) {
                    for (const org of apiData.organizations) {
                        const orgId = `org-${org.orgIdx}`;
                        this.$set(this.organizations, orgId, {
                            ...org,
                            orgBoundaries: [],
                            operBoundaries: [],
                        });
                    }
                    // 조직 목록만 로드; 경계는 조직 선택 시 loadOrgBoundaries / loadOperBoundaries 로 로드
                }
            } catch (error) {
                console.error("[BoundaryMain] 조직 목록 조회 실패:", error);
            } finally {
                this.loading = false;
            }
        },

        /** 조직 선택 시 해당 조직의 조직경계·운영경계 로드 (재선택 시에도 항상 API 호출하여 최신 데이터 표시) */
        async handleSelectOrg(orgId) {
            if (!orgId || !this.organizations[orgId]) return;

            const orgIdx = this.extractOrgIdx(orgId);
            if (orgIdx == null) return;

            this.loadingOrgId = orgId;
            try {
                await this.loadOrgBoundaries(orgIdx, orgId);
                await this.loadOperBoundaries(orgIdx, orgId);
            } catch (error) {
                console.error("[BoundaryMain] 조직 경계 로드 실패:", orgId, error);
            } finally {
                this.loadingOrgId = null;
            }
        },

        async loadOrgBoundaries(orgIdx, orgId) {
            try {
                const apiData = unwrapSuccess(
                    await carbonApi.orgBoundary.getOrgBoundaries(orgIdx, "ACTIVE"),
                    "조직경계 조회에 실패했습니다.",
                );

                if (apiData && apiData.boundaries) {
                    const orgBoundaries = [];

                    const boundaryPromises = apiData.boundaries.map(async (boundary) => {
                        try {
                            const facilities = await this.loadFacilities(boundary.orgBoundaryIdx);
                            const boundaryId = `boundary-${boundary.orgBoundaryIdx}`;

                            return {
                                ...boundary,
                                id: boundaryId,
                                facilities: facilities,
                            };
                        } catch (e) {
                            // 실패 시 빈 배열 할당
                            return {
                                ...boundary,
                                id: `boundary-${boundary.orgBoundaryIdx}`,
                                facilities: [],
                            };
                        }
                    });

                    const boundaryResults = await Promise.allSettled(boundaryPromises);

                    for (const result of boundaryResults) {
                        if (result.status === "fulfilled") {
                            orgBoundaries.push(result.value);
                        } else {
                            // 만약 예상치 못한 Promise chain 오류가 있을 경우 fallback 처리
                            // 이 코드는 일반적으로 도달하지 않지만 안전을 위해 추가
                            orgBoundaries.push({});
                        }
                    }

                    this.organizations[orgId].orgBoundaries = orgBoundaries;
                }
            } catch (error) {
                console.error("[BoundaryMain] 조직경계 조회 실패:", error);
            }
        },

        async loadFacilities(boundaryIdx) {
            try {
                const apiData = unwrapSuccess(
                    await carbonApi.orgBoundary.getFacilities(boundaryIdx),
                    "시설 매핑 조회에 실패했습니다.",
                );

                if (apiData && apiData.facilities) {
                    // API 응답을 그대로 반환
                    return apiData.facilities;
                }
                return [];
            } catch (error) {
                console.error("[BoundaryMain] 시설 매핑 조회 실패:", error);
                return [];
            }
        },

        async loadOperBoundaries(orgIdx, orgId) {
            try {
                const apiData = unwrapSuccess(
                    await carbonApi.operBoundary.getOperBoundaries(orgIdx),
                    "운영경계 조회에 실패했습니다.",
                );

                if (apiData && apiData.boundaries) {
                    const operBoundaries = [];

                    for (const boundary of apiData.boundaries) {
                        const categories = await this.loadCategories(boundary.operBoundaryIdx);
                        const boundaryId = `oper-${boundary.operBoundaryIdx}`;

                        // API 응답을 그대로 저장하되, categories만 추가
                        operBoundaries.push({
                            ...boundary,
                            id: boundaryId,
                            categories: categories,
                        });
                    }

                    this.organizations[orgId].operBoundaries = operBoundaries;
                }
            } catch (error) {
                console.error("[BoundaryMain] 운영경계 조회 실패:", error);
            }
        },

        async loadCategories(operBoundaryIdx) {
            try {
                const apiData = unwrapSuccess(
                    await carbonApi.operBoundary.getCategories(operBoundaryIdx),
                    "카테고리 조회에 실패했습니다.",
                );

                if (apiData && apiData.categories) {
                    // API 응답을 그대로 반환
                    return apiData.categories;
                }
                return [];
            } catch (error) {
                console.error("[BoundaryMain] 카테고리 조회 실패:", error);
                return [];
            }
        },

        async loadMasterCategories() {
            try {
                const apiData = unwrapSuccess(
                    await carbonApi.operBoundary.getAllCategories(),
                    "마스터 카테고리 조회에 실패했습니다.",
                );

                if (apiData && apiData.categories) {
                    // scope별로 분류
                    const scope1 = [];
                    const scope2 = [];
                    const scope3 = [];

                    apiData.categories.forEach((cat) => {
                        const scopeType = (cat.scopeType || "").toLowerCase();
                        const categoryItem = {
                            code: cat.categoryCode,
                            name: cat.categoryName,
                            scopeType,
                            categoryIdx: cat.categoryIdx,
                        };

                        if (scopeType === "scope1") {
                            scope1.push(categoryItem);
                        } else if (scopeType === "scope2") {
                            scope2.push(categoryItem);
                        } else if (scopeType === "scope3") {
                            scope3.push(categoryItem);
                        }
                    });

                    this.masterCategories.scope1 = scope1;
                    this.masterCategories.scope2 = scope2;
                    this.masterCategories.scope3 = scope3;
                } else {
                    // 응답에 categories가 없으면 기본값 사용
                    this.setDefaultCategories();
                }
            } catch (error) {
                console.error("[BoundaryMain] 마스터 카테고리 조회 실패:", error);
                this.setDefaultCategories();
            }
        },

        /** 운영경계 추가/편집 모달에서 사용하는 마스터 카테고리 — 첫 사용 시에만 로드 */
        async ensureMasterCategoriesLoaded() {
            const hasAny =
                (this.masterCategories.scope1 && this.masterCategories.scope1.length > 0) ||
                (this.masterCategories.scope2 && this.masterCategories.scope2.length > 0) ||
                (this.masterCategories.scope3 && this.masterCategories.scope3.length > 0);
            if (hasAny) return;
            await this.loadMasterCategories();
        },

        setDefaultCategories() {
            // 실패 시 기본 카테고리 사용 (scopeType은 API와 동일하게 소문자 사용)
            this.masterCategories.scope1 = getScope1Categories().map((cat) => ({
                code: cat.value,
                name: cat.text,
                scopeType: "scope1",
            }));
            this.masterCategories.scope2 = getScope2Categories().map((cat) => ({
                code: cat.value,
                name: cat.text,
                scopeType: "scope2",
            }));
            this.masterCategories.scope3 = getScope3Categories().map((cat) => ({
                code: cat.value,
                name: cat.text,
                scopeType: "scope3",
            }));
        },

        async save() {
            if (this.saving) return;

            if (!this.validateBoundarySettings()) {
                return;
            }

            this.saving = true;
            try {
                if (!this.organizations || Object.keys(this.organizations).length === 0) {
                    this.showToast("저장할 조직이 없습니다.", "info", "알림");
                    this.saving = false;
                    return;
                }

                const orgIds = Object.keys(this.organizations);

                for (const orgId of orgIds) {
                    const orgData = this.organizations[orgId];
                    if (!orgData) continue;

                    let orgIdx = orgData.orgIdx;

                    // 1. 조직이 없으면 생성
                    if (!orgIdx) {
                        try {
                            const createdOrgData = unwrapSuccess(
                                await carbonApi.organization.createOrg({
                                    orgName: orgData.orgName || orgData.name,
                                    orgCode: orgData.orgCode || "",
                                    businessNumber: orgData.businessNumber || "",
                                    industry: orgData.industry || "",
                                    address: orgData.address || "",
                                    contactPerson: orgData.contactPerson || "",
                                    contactEmail: orgData.contactEmail || "",
                                    contactPhone: orgData.contactPhone || "",
                                    fiscalYearEnd: orgData.fiscalYearEnd || "1231",
                                    carbonAccountingMethod: orgData.carbonAccountingMethod || "FINANCIAL_CONTROL",
                                }),
                                "조직 생성에 실패했습니다.",
                            );
                            orgIdx = createdOrgData.orgIdx;
                            orgData.orgIdx = orgIdx;
                        } catch (error) {
                            console.error("[BoundaryMain] 조직 생성 실패:", error);
                            continue;
                        }
                    }

                    if (!orgIdx || typeof orgIdx !== "number") {
                        console.error("[BoundaryMain] 유효하지 않은 orgIdx:", orgIdx);
                        continue;
                    }

                    // 2. 조직 경계 생성 또는 수정
                    for (const orgBoundary of orgData.orgBoundaries || []) {
                        if (!orgBoundary) continue;

                        const boundaryIdx = orgBoundary.orgBoundaryIdx || orgBoundary.boundaryIdx;

                        try {
                            if (boundaryIdx) {
                                // 기존 조직경계 수정 (PUT)
                                const boundaryUpdate = {
                                    orgBoundaryName: orgBoundary.orgBoundaryName || null,
                                    accountingMethod: orgBoundary.accountingMethod || "OPERATIONAL_CONTROL",
                                    startDate: orgBoundary.startDate || null,
                                    endDate: orgBoundary.endDate || null,
                                    organizationScope: orgBoundary.organizationScope || null,
                                    boundaryRationale: orgBoundary.boundaryRationale || "",
                                    remarks: orgBoundary.remarks || null,
                                    ...(orgBoundary.accountingMethod === "EQUITY_SHARE" && {
                                        sharePct: parseFloat(orgBoundary.sharePct || 100),
                                    }),
                                };

                                await carbonApi.orgBoundary.updateOrgBoundary(boundaryIdx, boundaryUpdate);

                                // 시설 매핑 저장
                                if (orgBoundary.facilities && orgBoundary.facilities.length > 0) {
                                    const facilities = orgBoundary.facilities
                                        .filter((f) => f && (f.facilityCode || f.facilityName))
                                        .map((f) => ({
                                            facilityCode: f.facilityCode || `FAC-${Date.now()}`,
                                            facilityName: f.facilityName || "",
                                            includePct: parseFloat(f.includePct || f.sharePct || 100),
                                            remarks: f.remarks || "",
                                        }));

                                    await unwrapSuccess(
                                        await carbonApi.orgBoundary.saveFacilities(boundaryIdx, { facilities }),
                                        "시설 매핑 저장에 실패했습니다.",
                                    );
                                }
                            } else {
                                // 새로운 조직경계 생성 (POST)
                                const orgBoundaryData = {
                                    orgBoundaryName: orgBoundary.orgBoundaryName || null,
                                    orgIdx: parseInt(orgIdx),
                                    accountingMethod: orgBoundary.accountingMethod || "OPERATIONAL_CONTROL",
                                    startDate: orgBoundary.startDate || new Date().toISOString().split("T")[0],
                                    endDate: orgBoundary.endDate || null,
                                    organizationScope: orgBoundary.organizationScope || orgBoundary.name || "조직경계",
                                    boundaryRationale: orgBoundary.boundaryRationale || "",
                                    remarks: orgBoundary.remarks || null,
                                    draftStat: "ACTIVE",
                                    // worker: this.loginUser.userId,
                                    ...(orgBoundary.accountingMethod === "EQUITY_SHARE" && {
                                        sharePct: parseFloat(orgBoundary.sharePct || 100),
                                    }),
                                };

                                const createdBoundary = unwrapSuccess(
                                    await carbonApi.orgBoundary.createOrgBoundary(parseInt(orgIdx), orgBoundaryData),
                                    "조직경계 생성에 실패했습니다.",
                                );
                                orgBoundary.orgBoundaryIdx = createdBoundary.orgBoundaryIdx;
                                orgBoundary.boundaryIdx = createdBoundary.orgBoundaryIdx;

                                // 시설 매핑 저장
                                if (orgBoundary.facilities && orgBoundary.facilities.length > 0) {
                                    const facilities = orgBoundary.facilities
                                        .filter((f) => f && (f.facilityCode || f.facilityName))
                                        .map((f) => ({
                                            facilityCode: f.facilityCode || `FAC-${Date.now()}`,
                                            facilityName: f.facilityName || "",
                                            includePct: parseFloat(f.includePct || f.sharePct || 100),
                                            remarks: f.remarks || "",
                                        }));

                                    if (facilities.length > 0) {
                                        await unwrapSuccess(
                                            await carbonApi.orgBoundary.saveFacilities(createdBoundary.orgBoundaryIdx, {
                                                facilities,
                                            }),
                                            "시설 매핑 저장에 실패했습니다.",
                                        );
                                    }
                                }
                            }
                        } catch (error) {
                            console.error("[BoundaryMain] 조직경계 저장 실패:", error);
                        }
                    }

                    // 3. 운영 경계 생성 또는 수정
                    for (const operBoundary of orgData.operBoundaries || []) {
                        if (!operBoundary) continue;

                        const operBoundaryIdx = operBoundary.operBoundaryIdx;

                        try {
                            if (operBoundaryIdx) {
                                // 기존 운영경계 수정 (PUT)
                                const operUpdate = {
                                    operBoundaryName: operBoundary.operBoundaryName || null,
                                    scope1: operBoundary.scope1 || 0,
                                    scope2: operBoundary.scope2 || 0,
                                    scope3: operBoundary.scope3 || 0,
                                    operationalScope: operBoundary.operationalScope || null,
                                    scopeRationale: operBoundary.scopeRationale || "",
                                    startDate: operBoundary.startDate || null,
                                    endDate: operBoundary.endDate || null,
                                    remarks:
                                        typeof operBoundary.remarks === "string"
                                            ? operBoundary.remarks.trim() || null
                                            : operBoundary.remarks || null,
                                };

                                await unwrapSuccess(
                                    await carbonApi.operBoundary.updateOperBoundary(operBoundaryIdx, operUpdate),
                                    "운영경계 수정에 실패했습니다.",
                                );

                                // 카테고리는 별도로 저장하지 않음 (카테고리 체크박스에서 즉시 저장됨)
                            } else {
                                // 새로운 운영경계 생성 (POST)
                                const operBoundaryData = {
                                    operBoundaryName: operBoundary.operBoundaryName || null,
                                    orgIdx: parseInt(orgIdx),
                                    scope1: operBoundary.scope1 || 0,
                                    scope2: operBoundary.scope2 || 0,
                                    scope3: operBoundary.scope3 || 0,
                                    operationalScope: operBoundary.operationalScope || operBoundary.name || "운영경계",
                                    scopeRationale: operBoundary.scopeRationale || "",
                                    startDate: operBoundary.startDate || new Date().toISOString().split("T")[0],
                                    endDate: operBoundary.endDate || null,
                                    draftStat: "ACTIVE",
                                    remarks:
                                        typeof operBoundary.remarks === "string"
                                            ? operBoundary.remarks.trim() || null
                                            : operBoundary.remarks || null,
                                    // worker: this.loginUser.userId,
                                };

                                const createdOper = unwrapSuccess(
                                    await carbonApi.operBoundary.createOperBoundary(orgData.orgCode, operBoundaryData),
                                    "운영경계 생성에 실패했습니다.",
                                );
                                operBoundary.operBoundaryIdx = createdOper.operBoundaryIdx;

                                // 카테고리는 별도로 저장하지 않음 (카테고리 체크박스에서 즉시 저장됨)
                            }
                        } catch (error) {
                            console.error("[BoundaryMain] 운영경계 저장 실패:", error);
                        }
                    }
                }

                this.showToast("경계 설정 저장이 완료되었습니다.", "success", "저장 완료");
                this.$emit("boundary-saved", this.organizations);
            } catch (error) {
                console.error("[BoundaryMain] 저장 중 오류:", error);
                const errorMsg = extractErrorMessage(error, "일부 항목 저장 중 오류가 발생했습니다.");
                this.showToast(errorMsg, "warning", "저장 경고");
            } finally {
                this.saving = false;
            }
        },

        validateBoundarySettings() {
            if (!this.organizations) {
                console.warn("[BoundaryMain] 조직이 없습니다.");
            }
            return true;
        },

        // ==================== 조직 관리 모달 ====================
        generateUniqueOrgId() {
            return `org-${Date.now()}-${this.nextOrgId++}`;
        },

        openOrgManagementModal(orgId) {
            if (orgId && this.organizations && this.organizations[orgId]) {
                this.orgModalMode = "edit";
                this.orgModalEditingId = orgId;
                const orgData = this.organizations[orgId];

                this.orgModalData = {
                    name: orgData?.orgName || orgData?.name || "",
                    orgCode: orgData?.orgCode || "",
                    businessNumber: orgData?.businessNumber || "",
                    industry: orgData?.industry || "",
                    orgIdx: orgData?.orgIdx || null,
                    address: orgData?.address || "",
                    remarks: orgData?.remarks || "",
                    contactPerson: orgData?.contactPerson || "",
                    contactEmail: orgData?.contactEmail || "",
                    contactPhone: orgData?.contactPhone || "",
                    fiscalYearEnd: orgData?.fiscalYearEnd || "1231",
                    accountingMethod:
                        orgData?.accountingMethod || orgData?.carbonAccountingMethod || "FINANCIAL_CONTROL",
                };
            } else {
                this.orgModalMode = "create";
                this.orgModalEditingId = null;
                this.orgModalData = {
                    name: "",
                    orgCode: "",
                    businessNumber: "",
                    industry: "",
                    address: "",
                    remarks: "",
                    contactPerson: "",
                    contactEmail: "",
                    contactPhone: "",
                    fiscalYearEnd: "1231",
                    accountingMethod: "FINANCIAL_CONTROL",
                };
            }
            this.showOrgManagementModal = true;
        },

        saveOrgManagement() {
            if (!this.orgModalData || !this.orgModalData.name?.trim()) {
                this.showToast("조직명을 입력해주세요.", "warning", "입력 필요");
                return;
            }

            if (!this.orgModalData.orgCode?.trim()) {
                this.orgModalData.orgCode = `ORG-${Date.now()}`;
            }

            if (!this.orgModalData.carbonAccountingMethod) {
                this.orgModalData.carbonAccountingMethod = "FINANCIAL_CONTROL";
            }
            if (!this.orgModalData.fiscalYearEnd) {
                this.orgModalData.fiscalYearEnd = "1231";
            }

            if (this.orgModalMode === "create") {
                const newOrgId = this.generateUniqueOrgId();
                this.$set(this.organizations, newOrgId, {
                    orgName: this.orgModalData.name.trim(),
                    orgCode: this.orgModalData.orgCode.trim(),
                    businessNumber: this.orgModalData.businessNumber?.trim() || "",
                    industry: this.orgModalData.industry?.trim() || "",
                    address: this.orgModalData.address?.trim() || "",
                    remarks: this.orgModalData.remarks?.trim() || "",
                    contactPerson: this.orgModalData.contactPerson?.trim() || "",
                    contactEmail: this.orgModalData.contactEmail?.trim() || "",
                    contactPhone: this.orgModalData.contactPhone?.trim() || "",
                    fiscalYearEnd: this.orgModalData.fiscalYearEnd || "1231",
                    accountingMethod: this.orgModalData.accountingMethod || "FINANCIAL_CONTROL",
                    orgIdx: null,
                    orgBoundaries: [],
                    operBoundaries: [],
                });

                this.showToast("조직이 추가되었습니다.", "success", "추가 완료");
            } else {
                if (!this.organizations || !this.organizations[this.orgModalEditingId]) {
                    this.showToast("조직을 찾을 수 없습니다.", "danger", "오류");
                    return;
                }
                const orgData = this.organizations[this.orgModalEditingId];
                orgData.orgName = this.orgModalData.name.trim();
                orgData.orgCode = this.orgModalData.orgCode.trim();
                orgData.businessNumber = this.orgModalData.businessNumber?.trim() || "";
                orgData.industry = this.orgModalData.industry?.trim() || "";
                orgData.address = this.orgModalData.address?.trim() || "";
                orgData.remarks = this.orgModalData.remarks?.trim() || "";
                orgData.contactPerson = this.orgModalData.contactPerson?.trim() || "";
                orgData.contactEmail = this.orgModalData.contactEmail?.trim() || "";
                orgData.contactPhone = this.orgModalData.contactPhone?.trim() || "";
                orgData.fiscalYearEnd = this.orgModalData.fiscalYearEnd || "1231";
                orgData.carbonAccountingMethod = this.orgModalData.carbonAccountingMethod || "FINANCIAL_CONTROL";
                orgData.orgBoundaries = (this.orgModalData.orgBoundaries || []).map((b) => ({
                    ...b,
                    facilities: (b?.facilities || []).map((f) => ({ ...f })),
                }));
                orgData.operBoundaries = (this.orgModalData.operBoundaries || []).map((ob) => ({
                    ...ob,
                    remarks: ob?.remarks || "",
                    categories: (ob?.categories || []).map((c) => ({ ...c })),
                }));

                this.showToast("조직이 수정되었습니다.", "success", "수정 완료");
            }

            this.showOrgManagementModal = false;
        },

        cancelOrgManagement() {
            this.showOrgManagementModal = false;
        },

        // 조직 필드 업데이트
        updateOrgModalField(field, value) {
            this.$set(this.orgModalData, field, value);
        },

        confirmRemoveOrganization(orgId) {
            if (!this.organizations || !this.organizations[orgId]) return;

            const orgName = this.organizations[orgId].name;
            this.$bvModal
                .msgBoxConfirm(`"${orgName}" 조직과 모든 조직경계 및 운영경계를 삭제하시겠습니까?`, {
                    title: "조직 삭제 확인",
                    okVariant: "danger",
                    okTitle: "삭제",
                    cancelTitle: "취소",
                    centered: true,
                })
                .then((value) => {
                    if (value) {
                        this.removeOrganization(orgId);
                    }
                });
        },

        removeOrganization(orgId) {
            if (!this.organizations) return;
            this.$delete(this.organizations, orgId);
            this.showToast("조직이 삭제되었습니다.", "success", "삭제 완료");
        },

        // 조직 상태 로컬 업데이트 (즉시 반영, API 호출 전)
        updateOrgStatusLocal({ orgId, status }) {
            if (this.organizations && this.organizations[orgId]) {
                this.$set(this.organizations[orgId], "status", status);
            }
        },

        // 경계 상태 로컬 업데이트 (즉시 반영, API 호출 전)
        updateBoundaryStatusLocal({ type, boundaryId, orgId, status }) {
            if (!this.organizations || !this.organizations[orgId]) return;

            if (type === "org") {
                // 조직경계 상태 업데이트
                const boundaries = this.organizations[orgId].orgBoundaries || [];
                const boundary = boundaries.find((b) => b.id === boundaryId);
                if (boundary) {
                    this.$set(boundary, "draftStat", status);
                }
            } else if (type === "oper") {
                // 운영경계 상태 업데이트
                const boundaries = this.organizations[orgId].operBoundaries || [];
                const boundary = boundaries.find((b) => b.id === boundaryId);
                if (boundary) {
                    this.$set(boundary, "draftStat", status);
                }
            }
        },

        // 조직 업데이트 핸들러 (API 응답 데이터 반영)
        handleOrgUpdated({ orgId, orgData }) {
            if (!this.organizations || !this.organizations[orgId]) return;

            // API 응답 데이터로 조직 정보 업데이트 (경계 정보는 유지)
            const currentOrgBoundaries = this.organizations[orgId].orgBoundaries || [];
            const currentOperBoundaries = this.organizations[orgId].operBoundaries || [];

            this.$set(this.organizations, orgId, {
                ...orgData,
                orgBoundaries: currentOrgBoundaries,
                operBoundaries: currentOperBoundaries,
            });

            this.showToast("조직 상태가 변경되었습니다.", "success", "상태 변경");
        },

        // 경계 상태 변경 완료 핸들러 (API 성공 후)
        handleBoundaryStatusChanged() {
            // 재로드 없이 토스트만 표시
            this.showToast("경계 상태가 변경되었습니다.", "success", "상태 변경");
        },

        handleStatusToggleError(error) {
            console.error("[BoundaryMain] 상태 토글 오류:", error);
            const message = extractErrorMessage(error, "상태 변경에 실패했습니다.");
            this.showToast(message, "danger", "오류");
        },

        // ==================== 삭제 핸들러 ====================
        handleOrganizationDeleted({ orgId, message }) {
            // 로컬 상태에서 제거
            if (this.organizations[orgId]) {
                this.$delete(this.organizations, orgId);
            }

            const toastMessage = message || "조직이 삭제되었습니다.";
            this.showToast(toastMessage, "success", "삭제 완료");
        },

        handleOrgBoundaryDeleted({ orgId, message }) {
            // 해당 조직의 조직경계만 재로드
            if (orgId) {
                const orgIdx = this.extractOrgIdx(orgId);
                this.reloadOrgBoundaries(orgIdx);
            }

            const toastMessage = message || "조직경계가 삭제되었습니다.";
            this.showToast(toastMessage, "success", "삭제 완료");
        },

        handleOperBoundaryDeleted({ orgId, message }) {
            // 해당 조직의 운영경계만 재로드
            if (orgId) {
                const orgIdx = this.extractOrgIdx(orgId);
                this.reloadOperBoundaries(orgIdx);
            }

            const toastMessage = message || "운영경계가 삭제되었습니다.";
            this.showToast(toastMessage, "success", "삭제 완료");
        },

        async handleDeleteError({ type, error }) {
            console.error("[BoundaryMain] 삭제 오류:", type, error);

            const errorMsg = extractErrorMessage(error, "삭제에 실패했습니다.");
            const errorTitle = "삭제 실패";

            // alertWarning 팝업 표시
            await this.alertWarning(errorMsg, errorTitle);
        },

        // ==================== 편집 모달 ====================
        openOrgEditModal({ orgId, orgData }) {
            this.editingOrgId = orgId;
            this.editingOrgData = { ...orgData };
            this.showOrgEditModal = true;
        },

        openOrgEvidenceModal({ org }) {
            if (!org?.orgIdx) return;
            this.orgEvidenceTarget = {
                orgIdx: org.orgIdx,
                orgName: org.orgName || org.name,
                name: org.orgName || org.name,
            };
            this.showOrgEvidenceModal = true;
        },

        handleOrgEvidenceUpdated() {
            // 증빙 업로드/삭제 후 별도 갱신 없음 (목록은 EvidenceModal 내부에서 갱신됨)
        },

        openOrgBoundaryEditModal({ boundary, orgId }) {
            this.editingBoundaryOrgId = orgId;
            this.editingBoundaryData = { ...boundary };
            this.showOrgBoundaryEditModal = true;
        },

        openOrgBoundaryAddModal({ orgId }) {
            this.editingBoundaryOrgId = orgId;
            // 빈 조직경계 데이터 생성
            this.editingBoundaryData = {
                orgBoundaryName: "",
                accountingMethod: "OPERATIONAL_CONTROL",
                sharePct: 100,
                startDate: null,
                endDate: null,
                scopeRationale: "",
            };
            this.showOrgBoundaryEditModal = true;
        },

        async openOperBoundaryEditModal({ boundary, orgId }) {
            await this.ensureMasterCategoriesLoaded();
            this.editingOperBoundaryOrgId = orgId;

            let categories = [];
            if (boundary.operBoundaryIdx) {
                try {
                    const responseData = unwrapSuccess(
                        await carbonApi.operBoundary.getCategories(boundary.operBoundaryIdx),
                        "운영경계 카테고리 조회에 실패했습니다.",
                    );
                    categories = responseData.categories || [];
                } catch (error) {
                    console.error("[BoundaryMain] 운영경계 카테고리 로드 실패:", error);
                }
            }

            // scope1/2/3 제거 후 전달. Scope는 모달에서 categories만 보고 추출함.
            // eslint-disable-next-line no-unused-vars
            const { scope1, scope2, scope3, ...rest } = boundary;
            this.editingOperBoundaryData = {
                ...rest,
                categories,
            };
            this.showOperBoundaryEditModal = true;
        },

        async openOperBoundaryAddModal({ orgId }) {
            await this.ensureMasterCategoriesLoaded();
            this.editingOperBoundaryOrgId = orgId;
            // 빈 운영경계 데이터 생성 (모든 Scope가 선택되지 않은 상태)
            this.editingOperBoundaryData = {
                operBoundaryName: "",
                scope1: 0,
                scope2: 0,
                scope3: 0,
                operationalScope: "",
                scopeRationale: "",
                startDate: null,
                endDate: null,
                remarks: "",
                categories: [],
            };
            this.showOperBoundaryEditModal = true;
        },

        // 조직 편집 완료
        // eslint-disable-next-line no-unused-vars
        async handleOrgEditSaved({ orgIdx, orgData }) {
            // 해당 조직만 업데이트
            if (this.editingOrgId && this.organizations[this.editingOrgId]) {
                const currentBoundaries = this.organizations[this.editingOrgId];
                this.$set(this.organizations, this.editingOrgId, {
                    ...orgData,
                    orgBoundaries: currentBoundaries.orgBoundaries || [],
                    operBoundaries: currentBoundaries.operBoundaries || [],
                });
            }

            this.showToast("조직이 수정되었습니다.", "success", "수정 완료");
        },

        // 조직경계 편집 완료
        async handleOrgBoundaryEditSaved({ response }) {
            // 해당 조직의 조직경계만 재로드
            if (this.editingBoundaryOrgId) {
                const orgIdx = this.extractOrgIdx(this.editingBoundaryOrgId);
                await this.reloadOrgBoundaries(orgIdx);
            }

            // response에서 boundaryIdx가 있으면 새로 추가된 것, 없으면 수정된 것
            const isNew = response?.data?.orgBoundaryIdx || response?.orgBoundaryIdx;
            if (isNew) {
                this.showToast("조직경계가 추가되었습니다.", "success", "추가 완료");
            } else {
                this.showToast("조직경계가 수정되었습니다.", "success", "수정 완료");
            }
        },

        // 운영경계 편집 완료
        async handleOperBoundaryEditSaved() {
            // 해당 조직의 운영경계만 재로드
            if (this.editingOperBoundaryOrgId) {
                const orgIdx = this.extractOrgIdx(this.editingOperBoundaryOrgId);
                await this.reloadOperBoundaries(orgIdx);
            }
        },

        extractOrgIdx(orgId) {
            // orgId에서 숫자만 추출 (예: "org-123" -> 123)
            if (typeof orgId === "number") return orgId;
            if (typeof orgId === "string" && orgId.startsWith("org-")) {
                const match = orgId.match(/org-(\d+)/);
                return match ? parseInt(match[1]) : null;
            }
            return null;
        },

        // UI 헬퍼
        showToast(message, variant = "info", title = "알림") {
            this.$bvToast.toast(message, { variant, title });
        },
    },
};
</script>

<style scoped>
/* 기본 스타일 리셋 */
label {
    margin-bottom: 0px !important;
}

/* 메인 컨테이너 */
.boundary-wizard {
    color: #1e293b;
    padding: 0;
    height: 100%;
}

/* 패널 스타일 */
.boundary-panel {
    background: white;
    border-radius: 12px;
    transition: all 0.3s ease;
    height: 100%;
}

.organization-section {
    overflow: visible;
    height: 100%;
}

.panel-title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.panel-title i {
    color: #64748b;
    font-size: 14px;
}

.panel-subtitle {
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    /* margin-bottom: 16px; */
    padding-bottom: 12px;
    border-bottom: 2px solid #e2e8f0;
}

.panel-content {
    padding: 0;
    overflow: visible;
    height: 100%;
}

.empty-organization {
    padding: 40px 20px;
    text-align: center;
    color: #94a3b8;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
}

.empty-organization i {
    font-size: 32px;
    margin-bottom: 12px;
    color: #cbd5e1;
}

.empty-organization p {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
}

/* 액션 버튼 */
.action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.action-btn {
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    display: flex;
    align-items: center;
    gap: 6px;
}

.btn-primary {
    background: #52b788;
    color: white;
    border: 1px solid #52b788;
}

.btn-primary:hover:not(:disabled) {
    background: #2d8659;
    border-color: #2d8659;
    box-shadow: 0 2px 4px rgba(82, 183, 136, 0.2);
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #e2e8f0;
    border-color: #e2e8f0;
    color: #94a3b8;
}

/* 반응형 */
@media (max-width: 768px) {
    .action-buttons {
        margin: 20px 12px 0;
        flex-direction: column;
    }

    .action-btn {
        width: 100%;
        justify-content: center;
    }
}
</style>
